import React, { useState } from 'react';
import { Button, MenuItem, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const read = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};

export function useDirectoryColumns(scope, available, defaults) {
  const key = `directory:${scope}:columns`;
  const [selection, setSelection] = useState(() => ({ key, value: read(key, null) }));
  const stored = selection.key === key ? selection.value : read(key, null);
  const columns = Array.isArray(stored) ? available.filter((id) => stored.includes(id)) : defaults;
  const setColumns = (values) => {
    const next = available.filter((id) => values.includes(id));
    setSelection({ key, value: next });
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* Private browsing may deny storage. */ }
  };
  return [columns, setColumns];
}

export function DirectoryColumns({ options, value, onChange, disabled = false }) {
  const { t } = useTranslation('directories');
  return <details className="dr-preferences"><summary>{t('columns')}</summary>
    <div className="dr-toolbar">{options.map(([id, label]) => <FormControlLabel key={id} label={label}
      control={<Checkbox checked={value.includes(id)} disabled={disabled} onChange={(_, checked) =>
        onChange(checked ? [...value, id] : value.filter((key) => key !== id))} />} />)}</div>
  </details>;
}

export default function DirectorySavedSearches({ scope, context, onRestore, disabled = false }) {
  const { t } = useTranslation('directories');
  const location = useLocation();
  const [, setParams] = useSearchParams();
  const key = `directory:${scope}:searches`;
  const [entries, setEntries] = useState(() => {
    const saved = read(key, []);
    return Array.isArray(saved) ? saved.filter((entry) => typeof entry?.name === 'string' &&
      typeof entry?.query === 'string' && entry.path === location.pathname).slice(0, 10) : [];
  });
  const [name, setName] = useState('');
  const [selected, setSelected] = useState('');
  const [storageError, setStorageError] = useState(false);
  const persist = (next) => {
    try {
      localStorage.setItem(key, JSON.stringify(next));
      setEntries(next);
      setStorageError(false);
      return true;
    } catch {
      setStorageError(true);
      return false;
    }
  };
  return <details className="dr-panel dr-preferences">
    <summary>{t('savedSearches')}</summary>
    <p className="dr-muted">{t('localPreferences')}</p>
    <div className="dr-toolbar">
      <TextField label={t('searchName')} value={name} inputProps={{ maxLength: 60 }} disabled={disabled}
        onChange={(event) => setName(event.target.value)} />
      <Button disabled={disabled || !name.trim()} onClick={() => {
        const query = new URLSearchParams(location.search);
        ['page', 'player', 'compare', 'compareA', 'compareB'].forEach((field) => query.delete(field));
        const entry = { name: name.trim(), path: location.pathname, query: query.toString(), context };
        if (persist([...entries.filter((item) => item.name !== entry.name), entry].slice(-10))) {
          setSelected(entry.name);
          setName('');
        }
      }}>{t('saveSearch')}</Button>
      <TextField select label={t('savedSearches')} value={selected} disabled={disabled || !entries.length}
        onChange={(event) => setSelected(event.target.value)}>
        <MenuItem value="">{t('chooseSearch')}</MenuItem>
        {entries.map((entry) => <MenuItem key={entry.name} value={entry.name}>{entry.name}</MenuItem>)}
      </TextField>
      <Button disabled={disabled || !entries.some((entry) => entry.name === selected)} onClick={() => {
        const entry = entries.find((item) => item.name === selected);
        if (!entry) return;
        if (entry.context && onRestore) onRestore(entry.context);
        setParams(new URLSearchParams(entry.query), { replace: true });
      }}>{t('applySearch')}</Button>
      <Button disabled={disabled || !selected} onClick={() => {
        if (persist(entries.filter((entry) => entry.name !== selected))) setSelected('');
      }}>{t('deleteSearch')}</Button>
    </div>
    {storageError && <p role="alert">{t('storageError')}</p>}
  </details>;
}
